import React, { useEffect, useState } from "react";
import Icon from "../../../components/Icon";
import PanelTemplate from "../../../templates/Panel";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import Button from "../../../components/Button";
import BoxShrinkable from "../../../components/BoxShrinkable";
import Input from "../../../components/Input";
import InputSelect from "../../../components/InputSelect";
import temperatureOptions from "../../../seeds/crm/temperatures";
import BoxTabs from "../../../components/BoxTabs";
import { toast } from "react-toastify";
import { getLabelFromOptions } from "../../../helpers/selects";
import moment from "moment";
import { convertDateToRelativeTime, convertIntToMoney, convertMoenyToInt } from "../../../helpers/conversions";
import InputDate from "../../../components/InputDate";
import origins from "../../../seeds/origins";
import segmentsOptions from "../../../seeds/segments";
import employeeRangesOptions from "../../../seeds/employeeRanges";
import SelectCompany from "../../../components/selects/SelectCompany";
import SelectCustomer from "../../../components/selects/SelectCustomer";
import SelectAdmin from "../../../components/selects/SelectAdmin";
import positionsOptions from "../../../seeds/positions";
import departmentsOptions from "../../../seeds/departments";
import CRMTimeline from "./Timeline";
import CRMTasks from "./Tasks";
import statusOptions from "../../../seeds/crm/status";
import { ModalDelete } from "../../../components/ModalDelete";
import CardTagsField from "./CardTagsField";

export const CRMCard = () => {
    const { id } = useParams();

    const navigate = useNavigate();
    const location = useLocation();

    const [gettingCard, setGettingCard] = useState(true);
    const [updatingCard, setUpdatingCard] = useState(false);
    const [card, setCard] = useState(undefined);
    
    const [gettingCompany, setGettingCompany] = useState(true);
    const [company, setCompany] = useState(null);

    const [gettingCustomer, setGettingCustomer] = useState(true);
    const [customer, setCustomer] = useState(null);

    const [confirmDelete, setConfirmDelete] = useState(false);

    const [tags, setTags] = useState([]);

    const loadTags = () => api.get('/crm-tag').then(res => {
        setTags(res.data.resources ?? []);
        return res.data.resources ?? [];
    }).catch(error => {
        renderError(error);
        return [];
    });

    useEffect(() => {
        if(id){
            getCard();
        }
    }, [id]);

    useEffect(() => {
        loadTags();
    }, []);

    useEffect(() => {
        if(card){
            getCompany();
            getCustomer();
        }
    }, [card]);

    const getCustomer = async () => {
        if(customer?.id !== card?.customer?.id){
            setGettingCustomer(true);
            await api.get(`/customer/${card.customer.id}`).then(res => {
                setCustomer(res.data.resource);
                setGettingCustomer(false);
            });
        }
        
        setGettingCustomer(false);
    }

    const getCompany = async () => {
        if(company?.id !== card?.company?.id){
            setGettingCompany(true);
            await api.get(`/company/${card.company.id}`).then(res => {
                setCompany(res.data.resource);
                setGettingCompany(false);
            });

        }
        
        setGettingCompany(false);
    }

    const getCard = () => {
        setGettingCard(true);

        api.get(`/pipeline-stage-card/${id}`).then(res => {
            setCard(res.data.resource);
        }).catch(error => {
            renderError(error);
        }).then(() => setGettingCard(false));
    }

    const updateCardParam = async (param, value) => {
        setUpdatingCard(true);

        if(param === 'amount'){
            value = convertMoenyToInt(value);
        }

        let updatedCard = {
            ...card,
            [param]: value
        };

        return api.put(`/pipeline-stage-card/${card.id}`, updatedCard).then(res => {
            setCard(res.data.resource);
            return true;
        }).catch(error => {
            renderError(error);
            return false;
        }).then(result => {
            setUpdatingCard(false);
            return result;
        });
    }

    const updateCompanyParam = async (param, value) => {
        let updatedCompany = {
            ...company,
            [param]: value
        };

        return api.put(`/company/${company.id}`, updatedCompany).then(res => {
            setCompany(res.data.resource);
            return true;
        }).catch(error => {
            renderError(error);
            return false;
        });
    }

    const updateCustomerParam = async (param, value) => {
        // O GET /customer retorna `companies` (objetos) e não `company_ids`, e o
        // marcador (`type`) pode vir vazio. O backend (CustomerRequest) exige
        // `company_ids` presente e `type` obrigatório, então montamos o payload
        // como a tela de edição: derivamos os ids das empresas e herdamos os
        // marcadores das empresas vinculadas.
        const companies = customer.companies ?? [];
        const type = [...new Set([
            ...(customer.type ?? []),
            ...companies.flatMap(company => company.type ?? [])
        ])];

        let updatedCustomer = {
            ...customer,
            company_ids: companies.map(company => company.id),
            type,
            [param]: value
        };

        return api.put(`/customer/${customer.id}`, updatedCustomer).then(res => {
            setCustomer(res.data.resource);
            return true;
        }).catch(error => {
            renderError(error);
            return false;
        });
    }

    // Ganho/Perdido dependem do painel, não da origem do card: painéis "sem
    // resultado comercial" (card_status_mode = operational) só trabalham com
    // Aberto e Arquivado.
    const allowsCommercialResult = card?.pipeline_card_status_mode !== 'operational';

    // Volta pro painel restaurando-o exatamente como estava: quando o card foi
    // aberto a partir do painel (state `from: 'crm-board'`), usamos o histórico
    // (navigate(-1)) — a URL volta com os filtros e o painel restaura paginação
    // e scroll salvos no sessionStorage. Senão, navegamos direto pro painel.
    const backToBoard = () => {
        if (location.state?.from === 'crm-board') {
            navigate(-1);
            return;
        }

        navigate(card?.pipeline_id ? `/crm/painel/${card.pipeline_id}` : '/crm');
    }

    const deleteCard = () => {
        setUpdatingCard(true);

        api.delete(`/pipeline-stage-card/${card.id}`).then(res => {
            navigate(card?.pipeline_id ? `/crm/painel/${card.pipeline_id}` : '/crm');
        }).catch(error => {
            renderError(error);
        }).then(() => setUpdatingCard(false))
    }

    return (
        <PanelTemplate id="crm-card">
            <ModalDelete
                show={confirmDelete}
                action={deleteCard}
                close={() => setConfirmDelete(false)}
                loading={updatingCard}
            />
            <button type="button" className="card-back" onClick={backToBoard}>
                ← Voltar para o painel
            </button>
            {card &&
                <div className="header">
                    <div className="stages">
                        {(() => {
                            const currentIndex = card.stages.findIndex(s => s.is_current);
                            return card.stages.map((stage, index) => {
                                const state = stage.is_current
                                    ? 'active'
                                    : (currentIndex > -1 && index < currentIndex) ? 'done' : 'upcoming';
                                return (
                                    <button
                                        key={index}
                                        className={`stage ${state}`}
                                        onClick={() => updateCardParam('pipeline_stage_id', stage.id)}
                                        title={stage.name}
                                    >
                                        <div className="stage-info">
                                            <span className="stage-name">{stage.name}</span>
                                            <span className="stage-days">
                                                {stage.days_in_stage} dia{stage.days_in_stage === 1 ? '' : 's'}
                                            </span>
                                        </div>
                                    </button>
                                );
                            });
                        })()}
                    </div>
                    <div className="stage-infos">
                        {card.status !== 'open' &&
                            <div className="card-status">
                                Status do card <span className={card.status}>{getLabelFromOptions(statusOptions, card.status)}</span>
                            </div>
                        }
                        {card.status === 'open' &&
                            <div className="temperature-info">
                                {/* Oportunidade de conclusão da venda <span className={card.temperature}>{getLabelFromOptions(temperatureOptions, card.temperature)}</span> */}
                            </div>
                        }
                        <div className="card-actions">
                            {updatingCard &&
                                <span>Carregando...</span>
                            }
                            {!updatingCard &&
                                <Button 
                                    type={`link`} 
                                    size={`small`}
                                    svg={`delete`} 
                                    loading={updatingCard} 
                                    action={() => setConfirmDelete(true)}
                                />
                            }
                            {!updatingCard && card.status === 'open' &&
                                <>
                                    {allowsCommercialResult &&
                                        <>
                                            <Button
                                                type={`primary`}
                                                text={`Ganho`}
                                                size={`small`}
                                                svg={`check`}
                                                style={{ backgroundColor: '#1B9E77', boxShadow: 'none' }}
                                                action={() => updateCardParam('status', 'won')}
                                                loading={updatingCard}
                                            />
                                            <Button
                                                type={`secondary`}
                                                text={`Perdido`}
                                                size={`small`}
                                                action={() => updateCardParam('status', 'lost')}
                                                loading={updatingCard}
                                            />
                                            {/* <Button
                                                type={`primary`}
                                                text={`Congelado`}
                                                size={`small`}
                                                style={{ backgroundColor: '#3367B7'}}
                                                action={() => updateCardParam('status', 'frozen')}
                                                loading={updatingCard}
                                            /> */}
                                        </>
                                    }
                                    <Button
                                        type={`secondary`}
                                        text={`Arquivar`}
                                        size={`small`}
                                        action={() => updateCardParam('status', 'archived')}
                                        loading={updatingCard}
                                    />
                                </>
                            }
                            {!updatingCard && card.status !== 'open' &&
                                <Button
                                    type={`primary`}
                                    text={`Reabrir card`}
                                    size={`small`}
                                    action={() => updateCardParam('status', 'open')}
                                />
                            }
                        </div>
                    </div>
                </div>
            }
            {card &&
                <div className="body">
                    <div className="row">
                        <div className="col-4">
                            {(card.related_order || card.order_item)
                                ? <OrderHighlight order={card.related_order} item={card.order_item} />
                                : card.budget && <QuoteHighlight budget={card.budget} />
                            }
                            <BoxShrinkable title={<><Icon name="notebook-pen" size={18} /> Detalhes do card</>}>
                                {/* Código da oportunidade */}
                                <InputPreview
                                    label={`Código da oportunidade`}
                                    previewValue={card.code}
                                />
                                {/* <InputPreview
                                    type="select"
                                    label={`Temperatura`}
                                    previewValue={getLabelFromOptions(temperatureOptions, card.temperature)}
                                    value={card.temperature}
                                    change={async value => updateCardParam('temperature', value)}
                                    options={temperatureOptions}
                                /> */}
                                <InputPreview
                                    type="admin"
                                    label={`Responsável`}
                                    previewValue={card?.admin?.name ?? 'Sem responsável'}
                                    value={card?.admin?.id ?? null}
                                    pipelineId={card?.pipeline_id ?? null}
                                    change={async value => updateCardParam('admin_id', value)}
                                />
                                <CardTagsField
                                    card={card}
                                    tags={tags}
                                    onCardChange={setCard}
                                    onTagCreated={loadTags}
                                />
                                {!card.budget &&
                                    <InputPreview
                                        label={`Valor`}
                                        previewValue={convertIntToMoney(card.amount, true)}
                                        value={card.amount}
                                        change={async value => updateCardParam('amount', value)}
                                        mask={'convertToMoney'}
                                    />
                                }
                                <InputPreview 
                                    type="date"
                                    label={`Previsão de conclusão`}
                                    previewValue={card.closing_date ? convertDateToRelativeTime(card.closing_date) : 'Não informada'}
                                    value={card.closing_date}
                                    change={async value => updateCardParam('closing_date', value)}
                                />
                                
                                <InputPreview 
                                    label={`Data de cadastro`} 
                                    previewValue={convertDateToRelativeTime(card.created_at, true)}
                                />
                                <InputPreview 
                                    label={`Última atualização`} 
                                    previewValue={convertDateToRelativeTime(card.updated_at, true)}
                                />
                                {/* <InputPreview 
                                    label={`Origem`} 
                                    previewValue={getLabelFromOptions(origins, card.origin)}
                                />
                                 */}
                                {/* <InputSelect options={temperatureOptions} value={card.temperature} isDisabled={true} /> */}
                            </BoxShrinkable>

                            <BoxShrinkable title={<><Icon name="company" size={18} /> Empresa {company && <small>{company.filled_percentage}</small>}</>}>
                                <SelectCompany 
                                    key={company?.updated_at ?? 'company'}
                                    selectedCompany={card?.company?.id ?? null} 
                                    setSelectedCompany={value => updateCardParam('company_id', value)} 
                                />
                                {(gettingCompany || gettingCard) &&
                                    <p>Carregando dados...</p>
                                }
                                {!gettingCompany && !gettingCard && company &&
                                    <>
                                        <InputPreview 
                                            label={`Nome da empresa`}
                                            previewValue={company.trade_name}
                                            value={company.trade_name}
                                            change={async value => updateCompanyParam('trade_name', value)} 
                                        />
                                        <InputPreview 
                                            label={`Razão social`}
                                            previewValue={company.company_name}
                                            value={company.company_name}
                                            change={async value => updateCompanyParam('company_name', value)} 
                                        />
                                        {/* <InputPreview 
                                            type="select"
                                            label={`Segmento`}
                                            previewValue={getLabelFromOptions(segmentsOptions, company.segment)}
                                            value={company.segment}
                                            change={async value => updateCompanyParam('segment', value)} 
                                            options={segmentsOptions}
                                        />
                                        <InputPreview 
                                            type="select"
                                            label={`Número de funcionários`}
                                            previewValue={getLabelFromOptions(employeeRangesOptions, company.employee_range)}
                                            value={company.employee_range}
                                            change={async value => updateCompanyParam('employee_range', value)} 
                                            options={employeeRangesOptions}
                                        />
                                        <InputPreview
                                            label={`Website`}
                                            previewValue={<a href={company.website} target="_blank">{company.website}</a>}
                                            value={company.website}
                                            change={async value => updateCompanyParam('website', value)} 
                                        /> */}
                                        <InputPreview 
                                            label={`Telefone`}
                                            previewValue={company.phone}
                                            value={company.phone}
                                            change={async value => updateCompanyParam('phone', value)} 
                                            mask={`convertToPhone`}
                                        />

                                        <Link style={{ display: "inline-block", marginTop: 15, fontSize: 14}} to={`/company/edit/${company.id}`}>Mais detalhes</Link>
                                    </>
                                }
                            </BoxShrinkable>
                            <BoxShrinkable title={<><Icon name="card-user" size={18} /> Pessoa {customer && <small>{customer.filled_percentage}</small>}</>}>
                                <SelectCustomer 
                                    key={customer?.updated_at ?? 'customer'}
                                    selectedCustomer={card?.customer?.id ?? null} 
                                    setSelectedCustomer={value => updateCardParam('customer_id', value)} 
                                    companyId={null}
                                />
                                {(gettingCustomer || gettingCard) &&
                                    <p>Carregando dados...</p>
                                }
                                {!gettingCustomer && !gettingCard && customer &&
                                    <>
                                        <InputPreview 
                                            label={`Nome`}
                                            previewValue={customer.name}
                                            value={customer.name}
                                            change={async value => updateCustomerParam('name', value)} 
                                        />
                                        {/* <InputPreview 
                                            type={'select'}
                                            label={`Cargo`}
                                            previewValue={getLabelFromOptions(positionsOptions, customer.position)}
                                            value={customer.position}
                                            change={async value => updateCustomerParam('position', value)} 
                                            options={positionsOptions}
                                        />
                                        <InputPreview 
                                            type={'select'}
                                            label={`Departamento`}
                                            previewValue={getLabelFromOptions(departmentsOptions, customer.department)}
                                            value={customer.department}
                                            change={async value => updateCustomerParam('department', value)} 
                                            options={departmentsOptions}
                                        /> */}
                                        <InputPreview 
                                            label={`E-mail`}
                                            previewValue={customer.email}
                                            value={customer.email}
                                            change={async value => updateCustomerParam('email', value)} 
                                        />
                                        
                                        <InputPreview 
                                            label={`Telefone`}
                                            previewValue={customer.phone}
                                            value={customer.phone}
                                            change={async value => updateCustomerParam('phone', value)} 
                                            mask={`convertToPhone`}
                                        />

                                        <Link style={{ display: "inline-block", marginTop: 15, fontSize: 14}} to={`/customer/edit/${customer.id}`}>Mais detalhes</Link>
                                    </>
                                }
                            </BoxShrinkable>
                        </div>
                        <div className="col-8">
                            <BoxTabs
                                titles={[
                                    'Timeline',
                                    'Tarefas',
                                    // 'E-mails',
                                    // 'Ligações',
                                    // 'Whatsapp'
                                ]}
                                contents={[
                                    <CRMTimeline cardId={card.id} />,
                                    <CRMTasks cardId={card.id} />,
                                    // <p style={{ textAlign: 'center', marginTop: 25, marginBottom: 15}}>Nenhum registro encontrado.</p>,
                                    // <p style={{ textAlign: 'center', marginTop: 25, marginBottom: 15}}>Nenhum registro encontrado.</p>,
                                    // <p style={{ textAlign: 'center', marginTop: 25, marginBottom: 15}}>Nenhum registro encontrado.</p>
                                ]}
                            />
                        </div>
                    </div>
                </div>
            }
        </PanelTemplate>
    );
}

export default CRMCard;

// Bloco destacado para cards originados de um pedido / item de pedido.
// Fica "à parte" dos Detalhes do card, com a imagem do produto, dados do
// pedido e os valores (unitário, item e pedido) em destaque.
const OrderHighlight = ({ order, item }) => {
    const isProduct = !!item;

    return (
        <div className="order-highlight">
            <div className="order-highlight__head">
                <span className="order-highlight__eyebrow">
                    <Icon name={isProduct ? 'product' : 'order'} size={14} />
                    {isProduct ? 'Produto do pedido' : 'Pedido'}
                </span>
                {order?.status_name &&
                    <span className="order-highlight__status">
                        <i style={{ backgroundColor: order.status_color || '#7A7499' }} />
                        {order.status_name}
                    </span>
                }
            </div>

            {isProduct &&
                <div className="order-highlight__product">
                    <div className="order-highlight__thumb">
                        <img
                            src={item.image_url?.small}
                            alt={item.product_name}
                            onError={(e) => { e.target.src = require('../../../assets/images/svgs/default.svg').default; }}
                        />
                        {item.quantity != null &&
                            <span className="order-highlight__qty">{item.quantity}×</span>
                        }
                    </div>
                    <div className="order-highlight__product-info">
                        <strong>{item.subproduct_name || item.product_name}</strong>
                        {item.subproduct_name && item.product_name &&
                            <span>{item.product_name}</span>
                        }
                    </div>
                </div>
            }

            <div className="order-highlight__stats">
                {isProduct && item.price != null &&
                    <div className="order-highlight__stat">
                        <span>Valor unitário</span>
                        <strong>{convertIntToMoney(item.price, true)}</strong>
                    </div>
                }
                {isProduct && item.total != null &&
                    <div className="order-highlight__stat">
                        <span>Total do item</span>
                        <strong>{convertIntToMoney(item.total, true)}</strong>
                    </div>
                }
                {order?.total != null &&
                    <div className="order-highlight__stat">
                        <span>Total do pedido</span>
                        <strong>{convertIntToMoney(order.total, true)}</strong>
                    </div>
                }
            </div>

            {order &&
                <Link to={`/order/edit/${order.id}`} target="_blank" className="order-highlight__link">
                    Abrir pedido #{order.code}
                    <Icon name="external-link" size={14} />
                </Link>
            }
        </div>
    );
}

// Bloco destacado para cards originados de um orçamento. Mesma linguagem
// visual do OrderHighlight, mas sem imagem de produto (o orçamento agrega
// vários itens) — mostra código, quantidade de itens, status e total.
const QuoteHighlight = ({ budget }) => {
    return (
        <div className="order-highlight">
            <div className="order-highlight__head">
                <span className="order-highlight__eyebrow">
                    <Icon name="quote" size={14} />
                    Orçamento
                </span>
                {budget.status_name &&
                    <span className="order-highlight__status">
                        <i style={{ backgroundColor: budget.status_color || '#7A7499' }} />
                        {budget.status_name}
                    </span>
                }
            </div>

            <div className="order-highlight__product">
                <div className="order-highlight__icon">
                    <Icon name="quote" size={26} />
                </div>
                <div className="order-highlight__product-info">
                    <strong>Orçamento #{budget.code}</strong>
                    {budget.items_count != null &&
                        <span>{budget.items_count} {budget.items_count === 1 ? 'item' : 'itens'}</span>
                    }
                </div>
            </div>

            <div className="order-highlight__stats">
                {budget.total != null &&
                    <div className="order-highlight__stat order-highlight__stat--highlight">
                        <span>Total do orçamento</span>
                        <strong>{convertIntToMoney(budget.total, true)}</strong>
                    </div>
                }
            </div>

            <Link to={`/quote/edit/${budget.id}`} target="_blank" className="order-highlight__link">
                Abrir orçamento #{budget.code}
                <Icon name="external-link" size={14} />
            </Link>
        </div>
    );
}

const InputPreview = ({type, label, previewValue, copyValue, value, change, options, mask, link, linkNewTab, pipelineId}) => {
    const navigate = useNavigate();

    const [edit, setEdit] = useState(false);
    const [prevValue, setPrevValue] = useState(value);
    const [updating, setUpdating] = useState(false);

    const copyContent = async () => {
        try {
            await navigator.clipboard.writeText(copyValue ?? previewValue);
            toast.success("Texto copiado", { autoClose: 1500});
        } catch (err) {
            toast.error("Falha ao copiar o texto: ", err);
        }
    }

    const save = async () => {
        setEdit(false);
        setUpdating(true);
        let result = await change(prevValue);
        if(result){
            setUpdating(false);
            console.log('re', result);
        } else {
            setEdit(true);
            setUpdating(false);
            console.log('re2', result);
        }
    }

    return (
        <div className="input-preview">
            {edit &&
                <div className="box-edit">
                    {type === 'select' &&
                        <InputSelect options={options} value={prevValue} change={setPrevValue} />
                    }
                    {type === 'admin' &&
                        <SelectAdmin
                            selectedAdmin={prevValue}
                            setSelectedAdmin={setPrevValue}
                            nullable={true}
                            labelNullable={`Sem responsável`}
                            nullableValue={null}
                            pipelineId={pipelineId}
                        />
                    }
                    {(type === 'text' || !type) &&
                        <Input label={label} value={prevValue} change={setPrevValue} mask={mask ?? null} />
                    }
                    {type === 'date' &&
                        <InputDate 
                            format={`YYYY-MM-DD`} 
                            value={prevValue} 
                            change={setPrevValue} 
                            label={label} 
                        />
                    }
                    <div className="actions">
                        <Button
                            type={`secondary`}
                            text={`Cancelar`}
                            size={`small`}
                            action={() => setEdit(false)}
                        />
                        <Button
                            type={`primary`}
                            text={`Salvar`}
                            size={`small`}
                            svg={`save-white`}
                            action={save}
                        />
                    </div>
                </div>
            }
            <div className="content">
                <label>{label}</label>
                {link && linkNewTab
                    ? <p>
                        <a href={link} target="_blank" rel="noreferrer" className="preview-link">
                            {previewValue}
                            <Icon name="link" size={16} />
                        </a>
                    </p>
                    : <p>{updating ? 'Salvando...' : previewValue}</p>
                }
            </div>
            <div className="actions">
                {!linkNewTab &&
                    <button onClick={copyContent}><Icon name="input-preview-cp" size={16} /></button>
                }
                {typeof change === 'function' &&
                    <button onClick={() => setEdit(true)}><Icon name="input-preview-edit" size={16} /></button>
                }
                {link && !linkNewTab &&
                    <button onClick={() => navigate(link)}><Icon name="input-preview-edit" size={16} /></button>
                }
            </div>
        </div>
    );
}